import { CommonTrainInfoFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { CommonSubTypeProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoFormModal";
import { trainRouteStopToStopId } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/AffectedStopsFields";
import { TrainContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/formContextWrappers";
import { toNonExcludedStops } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/listofExcludedStations";
import { EitherTrainFormSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { useTrainInfoContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import { TrainRouteStop } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ChoiceChip, Flex, Skeleton, VStack } from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { Select } from "shared/components/forms/Select";

type SelectStopsFieldsProps = {
  stopsField: "extraStops" | "skippedStops";
  justPassingThough: boolean;
  label: string;
};

const BASE_CASE = "INVALID";

export const SelectStopsFields: FC<
  CommonTrainInfoFormProps &
    CommonSubTypeProps &
    TrainContext &
    SelectStopsFieldsProps
> = ({ trainId, trainFormPrefix, stopsField, justPassingThough, label }) => {
  const { t } = useTranslation();
  const { getValues, setValue, formState } =
    useFormContext<EitherTrainFormSchema>();
  const { errors } = formState;

  const stopsFieldPath = `${trainFormPrefix}.${stopsField}` as const;

  const { useTrainRouteBatch } = useTrainInfoContext();
  const { data: trainRoute, status: fetchStatus } = useTrainRouteBatch(trainId);

  const trainStops: TrainRouteStop[] = trainRoute?.plannedRouteStops ?? [];

  const filteredData = trainStops.filter(
    (stop) => stop.justPassingThrough === justPassingThough,
  );

  const selectableStops = toNonExcludedStops(filteredData).map((it) =>
    trainRouteStopToStopId(it, trainId.countryCode),
  );

  const selectedStops = getValues(stopsFieldPath) ?? [];

  if (fetchStatus === "pending") {
    return <Skeleton height="36px" />;
  }

  if (fetchStatus === "error" || !trainRoute || trainRoute.stops.length <= 0) {
    return (
      <FailureMessage
        customMessage={`${t("Kunne ikke hente ut stoppene til toget.")} ${t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}`}
      />
    );
  }

  return (
    <VStack width="100%" alignItems="flex-start" gap="18px" mb="12px">
      {selectedStops.length > 0 && (
        <Flex width="100%" gap="6px" wrap="wrap">
          {selectedStops.map((stop) => (
            <ChoiceChip
              key={stop.id}
              chipType="filter"
              size="sm"
              checked
              onCheckedChange={() => {
                const newSelectedStops = selectedStops.filter(
                  (s) => s.id !== stop.id,
                );
                setValue(stopsFieldPath, newSelectedStops);
              }}
            >
              {stop.name}
            </ChoiceChip>
          ))}
        </Flex>
      )}
      <Flex width="100%">
        <VStack gap={0} flexBasis={1} flexGrow={1} alignItems="stretch">
          <Select
            onChange={(e) => {
              const { value } = e.target;
              if (value === BASE_CASE) return;
              const selectedStop = selectableStops.find(
                (stop) => stop.id === value,
              );
              if (!selectedStop) return;
              const newSelectedStops = [...selectedStops, selectedStop];
              setValue(stopsFieldPath, newSelectedStops);
            }}
            label={`${t("Velg stasjon")}*`}
            data-testid="select_singularStop"
          >
            <option value={BASE_CASE}>{label}</option>
            {selectableStops
              .filter((stop) => !selectedStops.find((it) => it.id === stop.id))
              .map((stop) => (
                <option value={stop.id} key={stop.id}>
                  {stop.name}
                </option>
              ))}
          </Select>
          <RenderErrorInPath errors={errors} errorPath={stopsFieldPath} />
        </VStack>
      </Flex>
    </VStack>
  );
};
